import { useMemo } from "react"

import { asArray } from "@/lib/as-array"
import { deskSummary, groupDesk, type DeskGroup } from "@/lib/desk"
import type { Event, JobRisk, Task } from "openapi/api/model"
import { useGetEvents, useGetOrgObsJobRisks, useGetTasks } from "openapi/api/endpoints"

/**
 * The dashboard, from the desk — the Morning Desk redesign, on live data.
 *
 * `/dashboard` used to render `canvasDashboardData`: a fixture, so every
 * organization saw the same date, the same candidate names and the same
 * $247,400. `/` redirects here, which made sample data the product's front
 * door.
 *
 * What replaces it is not the same page with real numbers poured in. The old
 * one led with money — "Landed $84,500" in the largest type on the screen —
 * which is history, and the one figure a recruiter cannot act on. This one
 * answers a single question, *what do I do first today*, and everything above
 * the queue exists to justify the queue.
 *
 * There is deliberately no money block. A job carries no fee, so "in play" and
 * "committed" cannot be computed at all; landed revenue is real and lives on
 * Placements, where it is the subject rather than an ornament.
 */

export type DeskAppointment = {
  id: string
  /** "14:00", or "all day". */
  at: string
  title: string
  detail?: string
  /** Within the next couple of hours, so the claim mentions it. */
  soon: boolean
  /** Already happened — still listed, never announced as what is next. */
  past: boolean
}

export type MorningDesk = {
  claim: string
  /** The line under the claim, from which the claim is derived. */
  summary: ReturnType<typeof deskSummary>
  groups: DeskGroup[]
  today: DeskAppointment[]
  /** Due tasks and other routine items, as a count rather than a list. */
  routineCount: number
  isLoading: boolean
  isError: boolean
}

const SOON_MS = 2 * 60 * 60 * 1000

function clockOf(at?: string | null) {
  const parsed = Date.parse(at ?? "")
  if (!Number.isFinite(parsed)) return undefined
  return new Date(parsed)
}

function isToday(at: Date, now: Date) {
  return (
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate()
  )
}

/**
 * Robin's read of the desk, in sentences, derived only from the figures beneath
 * it. If it cannot be derived it is not said — a briefing that invents once is
 * never trusted again.
 */
function claimFor(
  summary: ReturnType<typeof deskSummary>,
  groups: DeskGroup[],
  next?: DeskAppointment,
) {
  if (summary.open === 0) {
    return "No open jobs on the desk."
  }

  const parts: string[] = []
  const starving = groups.find((group) => group.trouble === "starving")
  const stuck = groups.find((group) => group.trouble === "stuck")
  const leaking = groups.find((group) => group.trouble === "leaking")

  if (starving) {
    const n = starving.rows.length
    parts.push(
      `${n === 1 ? "One job has" : `${n} jobs have`} nobody at submit — ${
        n === 1 ? "it is" : "they are"
      } the ones that fail quietly.`,
    )
  }
  if (stuck) {
    const worst = stuck.rows[0]
    parts.push(
      worst.quietFor === undefined
        ? `${stuck.rows.length} stalled.`
        : `${worst.company ?? worst.title} has been quiet ${worst.quietFor} days.`,
    )
  }
  if (leaking) {
    parts.push(
      `${leaking.rows.length === 1 ? "One brief is" : `${leaking.rows.length} briefs are`} rejecting most of what it screens.`,
    )
  }
  if (parts.length === 0) {
    parts.push("Nothing is stuck and nothing is starving.")
  }
  if (next) {
    parts.push(`Next: ${next.at} — ${next.title}.`)
  }

  return parts.join(" ")
}

export function useMorningDesk(): MorningDesk {
  const risks = useGetOrgObsJobRisks()
  const tasks = useGetTasks()
  const events = useGetEvents()

  return useMemo(() => {
    const now = new Date()
    const jobs = asArray<JobRisk>(risks.data)

    const groups = groupDesk(jobs, now.getTime())
    const summary = deskSummary(jobs, now.getTime())

    type Dated = DeskAppointment & { sortAt: number }
    const today: DeskAppointment[] = asArray<Event>(events.data)
      .map((event): Dated | undefined => {
        const at = clockOf(event.start_at)
        if (!at || !isToday(at, now)) return undefined
        const from = at.getTime() - now.getTime()
        return {
          id: event.id ?? at.toISOString(),
          at: at.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          }),
          title: event.title ?? "Event",
          detail: event.location ?? undefined,
          // Close enough that the claim mentions it rather than leaving it to
          // be found further down the page.
          soon: from > 0 && from < SOON_MS,
          past: from <= 0,
          sortAt: at.getTime(),
        }
      })
      .filter((item): item is Dated => item !== undefined)
      .sort((a, b) => a.sortAt - b.sortAt)
      .map(({ sortAt: _sortAt, ...item }) => item)

    /*
      Tasks are a count rather than a list. They already have a page, and the
      queue below is what this screen is for — a second list of to-dos would
      compete with it.
    */
    const routineCount = asArray<Task>(tasks.data).filter((task) => {
      if (task.status === "done") return false
      /*
        Compared against the clock, not through `daysSince` — that helper floors
        at zero so a task due next March came back as `0` days old and counted
        as due today. "Due" means the date has arrived.
      */
      const due = Date.parse(task.due_date ?? "")
      return Number.isFinite(due) && due <= now.getTime()
    }).length

    return {
      /*
        The next appointment still ahead of us. `soon` alone was not enough: on
        an afternoon with nothing inside the two-hour window the fallback took
        `today[0]`, and the page announced "Next:" against a meeting that had
        already happened.
      */
      claim: claimFor(summary, groups, today.find((item) => !item.past)),
      summary,
      groups,
      today,
      routineCount,
      /*
        Every request, not just job-risks. An unresolved or failed `/events` or
        `/tasks` is indistinguishable on screen from an empty schedule, and the
        briefing would present itself as complete while missing both.
      */
      isLoading: risks.isPending || tasks.isPending || events.isPending,
      isError: risks.isError || tasks.isError || events.isError,
    }
  }, [
    risks.data,
    risks.isPending,
    risks.isError,
    tasks.data,
    tasks.isPending,
    tasks.isError,
    events.data,
    events.isPending,
    events.isError,
  ])
}
